import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SectionHeader } from '@/components/ui/section-header';
import {
  Loader2,
  Save,
  Eye,
  EyeOff,
  Check,
  AlertCircle,
  Settings,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Pencil,
  X,
  Cpu,
} from 'lucide-react';
import { useProviderTemplates, useProviders } from '@/lib/use-providers';
import { fetchTemplateModels } from '@/lib/provider-api';
import type {
  Provider,
  ProviderTemplate,
  ProviderModel,
  ConfigSchema,
  CustomModelFormData,
} from '@/lib/provider-types';

interface SettingsPanelProps {
  workspaceId: string | null;
}

/** Convert snake_case / camelCase key to human-readable label */
function formatLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function SettingsPanel({ workspaceId }: SettingsPanelProps) {
  const { templates, loading: templatesLoading } = useProviderTemplates();
  const {
    providers, loading: providersLoading, error,
    create, update, remove,
    addCustomModel, editCustomModel, removeCustomModel,
  } = useProviders(workspaceId);

  const [addingTemplateId, setAddingTemplateId] = useState<number | null>(null);
  const [expandedProviderId, setExpandedProviderId] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loading = templatesLoading || providersLoading;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader2 className="size-6 text-primary animate-spin mb-3" />
        <p className="text-sm text-muted-foreground">Loading providers...</p>
      </div>
    );
  }

  const configuredTemplateIds = new Set(providers.map((p) => p.providerTemplateId));
  const availableTemplates = templates.filter((t) => !configuredTemplateIds.has(t.id));

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <SectionHeader
        icon={<Settings className="size-4 text-primary" />}
        title="LLM Providers"
        subtitle="Configure AI provider credentials"
      />

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 pt-0 space-y-3">
        {error && (
          <div className="flex items-start gap-2 bg-destructive/10 text-destructive text-xs p-3 rounded-md border border-destructive/20">
            <AlertCircle className="size-3.5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="flex items-start gap-2 bg-green-500/10 text-green-600 dark:text-green-400 text-xs p-3 rounded-md border border-green-500/20">
            <Check className="size-3.5 shrink-0 mt-0.5" />
            <span>{successMessage}</span>
          </div>
        )}

        {/* Configured providers */}
        {providers.filter((p) => !p.isPlatformManaged).map((provider) => (
          <ProviderCard
            key={provider.id}
            provider={provider}
            expanded={expandedProviderId === provider.id}
            onToggle={() =>
              setExpandedProviderId(expandedProviderId === provider.id ? null : provider.id)
            }
            onUpdate={async (body) => {
              await update(provider.id, body);
              showSuccess(`${provider.displayName} updated`);
            }}
            onDelete={async () => {
              await remove(provider.id);
              showSuccess(`${provider.displayName} removed`);
            }}
            onAddCustomModel={async (data) => {
              await addCustomModel(provider.id, data);
              showSuccess('Custom model added');
            }}
            onEditCustomModel={async (modelId, data) => {
              await editCustomModel(provider.id, modelId, data);
              showSuccess('Custom model updated');
            }}
            onDeleteCustomModel={async (modelId) => {
              await removeCustomModel(provider.id, modelId);
              showSuccess('Custom model removed');
            }}
          />
        ))}

        {/* Platform-managed providers (read-only, collapsed) */}
        {providers.filter((p) => p.isPlatformManaged).map((provider) => (
          <ProviderCard
            key={provider.id}
            provider={provider}
            expanded={expandedProviderId === provider.id}
            onToggle={() =>
              setExpandedProviderId(expandedProviderId === provider.id ? null : provider.id)
            }
            onUpdate={async () => {}}
            onDelete={async () => {}}
            onAddCustomModel={async () => {}}
            onEditCustomModel={async () => {}}
            onDeleteCustomModel={async () => {}}
          />
        ))}

        {/* Add new provider form */}
        {addingTemplateId !== null && (
          <AddProviderForm
            template={templates.find((t) => t.id === addingTemplateId)!}
            onSave={async (body) => {
              await create(body);
              setAddingTemplateId(null);
              showSuccess('Provider added');
            }}
            onCancel={() => setAddingTemplateId(null)}
          />
        )}

        {/* Add provider button */}
        {addingTemplateId === null && availableTemplates.length > 0 && (
          <div className="space-y-2">
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
              Add Provider
            </p>
            <div className="grid gap-1.5">
              {availableTemplates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setAddingTemplateId(t.id)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-md border bg-card hover:bg-accent/50 transition-colors text-left"
                >
                  <Plus className="size-3.5 text-muted-foreground" />
                  <div>
                    <p className="text-xs font-medium">{t.name}</p>
                    {t.description && (
                      <p className="text-[10px] text-muted-foreground">{t.description}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {providers.length === 0 && addingTemplateId === null && (
          <div className="text-center py-8">
            <Settings className="size-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              No providers configured. Add one to start using AI models.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Provider Card (configured provider) ───────────────────

function ProviderCard({
  provider,
  expanded,
  onToggle,
  onUpdate,
  onDelete,
  onAddCustomModel,
  onEditCustomModel,
  onDeleteCustomModel,
}: {
  provider: Provider;
  expanded: boolean;
  onToggle: () => void;
  onUpdate: (body: { config?: Record<string, unknown>; name?: string; defaultProviderModelId?: number }) => Promise<void>;
  onDelete: () => Promise<void>;
  onAddCustomModel: (data: CustomModelFormData) => Promise<void>;
  onEditCustomModel: (modelId: number, data: Partial<CustomModelFormData>) => Promise<void>;
  onDeleteCustomModel: (modelId: number) => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const schema = provider.providerTemplate.configSchema;
  const allModels = provider.providerModels ?? provider.providerTemplate.models ?? [];
  // Sort: custom models first, then system models
  const sortedModels = [...allModels].sort((a, b) => {
    if (a.isCustom && !b.isCustom) return -1;
    if (!a.isCustom && b.isCustom) return 1;
    return 0;
  });
  const customModels = allModels.filter((m) => m.isCustom);

  // Pick initial default model
  const defaultModel = allModels.find((m) => m.id === provider.defaultProviderModelId)
    ?? allModels.find((m) => m.isDefault)
    ?? allModels[0];

  // Initialize selectedModelId: use defaultProviderModelId, or fall back to computed default
  const initialModelId = provider.defaultProviderModelId ?? defaultModel?.id ?? undefined;
  const [selectedModelId, setSelectedModelId] = useState<number | undefined>(initialModelId);
  const [customModelDialog, setCustomModelDialog] = useState<ProviderModel | 'new' | null>(null);
  const [confirmDeleteModel, setConfirmDeleteModel] = useState<number | null>(null);
  const isDirty = selectedModelId !== (provider.defaultProviderModelId ?? defaultModel?.id ?? undefined);

  const handleSave = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const body: Record<string, unknown> = {};
      if (selectedModelId !== (provider.defaultProviderModelId ?? undefined)) {
        body.defaultProviderModelId = selectedModelId;
      }
      if (Object.keys(body).length === 0) {
        setSaving(false);
        return;
      }
      await onUpdate(body);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 3000);
      return;
    }
    setDeleting(true);
    try {
      await onDelete();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Delete failed');
      setDeleting(false);
    }
  };

  const handleDeleteModel = async (modelId: number) => {
    if (confirmDeleteModel !== modelId) {
      setConfirmDeleteModel(modelId);
      setTimeout(() => setConfirmDeleteModel(null), 3000);
      return;
    }
    try {
      await onDeleteCustomModel(modelId);
      setConfirmDeleteModel(null);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to delete model');
    }
  };

  return (
    <div className="rounded-md border bg-card overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 w-full px-3 py-3 hover:bg-accent/30 transition-colors"
      >
        {expanded ? (
          <ChevronDown className="size-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-3.5 text-muted-foreground" />
        )}
        <span className="text-xs font-medium flex-1 text-left">
          {provider.displayName || provider.providerTemplate.name}
        </span>
        {defaultModel && (
          <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded truncate max-w-[120px]">
            {defaultModel.name}
          </span>
        )}
        {provider.isPlatformManaged && (
          <span className="text-[10px] text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 py-0.5 rounded">
            managed
          </span>
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t pt-3">
          {formError && (
            <div className="text-xs text-destructive bg-destructive/10 p-2 rounded">
              {formError}
            </div>
          )}

          {/* Default model selector */}
          {sortedModels.length > 0 && !provider.isPlatformManaged && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Default Model</Label>
              <select
                value={selectedModelId ?? ''}
                onChange={(e) => setSelectedModelId(e.target.value ? Number(e.target.value) : undefined)}
                className="w-full h-8 px-3 rounded-md border border-input bg-background text-xs"
              >
                <option value="">Select model...</option>
                {sortedModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}{m.isCustom ? ' (custom)' : ''} — {m.slug}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Available models list (read-only for platform-managed) */}
          {sortedModels.length > 0 && provider.isPlatformManaged && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Available Models ({sortedModels.length})</Label>
              <div className="space-y-1 max-h-48 overflow-auto">
                {sortedModels.map((m) => (
                  <div
                    key={m.id}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs ${
                      m.id === initialModelId
                        ? 'border border-primary/30 bg-primary/5'
                        : 'border bg-muted/30'
                    }`}
                  >
                    <Cpu className="size-3 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{m.name}</p>
                      <p className="text-[10px] text-muted-foreground truncate font-mono">{m.slug}</p>
                    </div>
                    {m.isCustom && (
                      <span className="text-[9px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
                        custom
                      </span>
                    )}
                    {m.id === initialModelId && (
                      <span className="text-[9px] text-primary bg-primary/10 px-1.5 py-0.5 rounded shrink-0">
                        default
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Config fields (read-only for existing providers, matching hai_assistants) */}
          {schema && (
            <ReadOnlyConfigFields
              schema={schema}
              maskedValues={provider.maskedSecretConfig}
            />
          )}

          {/* Custom Models section */}
          {!provider.isPlatformManaged && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">Custom Models</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 text-[10px] px-2"
                  onClick={() => setCustomModelDialog('new')}
                >
                  <Plus className="size-3 mr-1" />
                  Add Model
                </Button>
              </div>

              {customModels.length === 0 ? (
                <p className="text-[10px] text-muted-foreground py-2">
                  No custom models. Add one to use with this provider.
                </p>
              ) : (
                <div className="space-y-1">
                  {customModels.map((model) => (
                    <div
                      key={model.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded border bg-muted/30 text-xs"
                    >
                      <Cpu className="size-3 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{model.name}</p>
                        <p className="text-[10px] text-muted-foreground truncate">
                          {model.slug} — {model.maxTokens.toLocaleString()} max / {model.contextWindow.toLocaleString()} ctx
                        </p>
                      </div>
                      {model.id === provider.defaultProviderModelId && (
                        <span className="text-[9px] text-primary bg-primary/10 px-1.5 py-0.5 rounded shrink-0">
                          default
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => setCustomModelDialog(model)}
                        className="p-1 hover:bg-accent rounded transition-colors shrink-0"
                        title="Edit"
                      >
                        <Pencil className="size-3 text-muted-foreground" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteModel(model.id)}
                        className={`p-1 rounded transition-colors shrink-0 ${
                          confirmDeleteModel === model.id
                            ? 'bg-destructive/10 text-destructive'
                            : 'hover:bg-accent text-muted-foreground'
                        }`}
                        title={confirmDeleteModel === model.id ? 'Click again to confirm' : 'Delete'}
                      >
                        <Trash2 className="size-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            {!provider.isPlatformManaged && (
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving || deleting || !isDirty}
              >
                {saving ? (
                  <Loader2 className="size-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Save className="size-3.5 mr-1.5" />
                )}
                Save
              </Button>
            )}
            <div className="flex-1" />
            {!provider.isPlatformManaged && (
              <Button
                size="sm"
                variant={confirmDelete ? 'destructive' : 'outline'}
                onClick={handleDelete}
                disabled={saving || deleting}
              >
                <Trash2 className="size-3.5 mr-1.5" />
                {confirmDelete ? 'Confirm?' : 'Remove'}
              </Button>
            )}
          </div>

          {/* Custom Model Dialog */}
          {customModelDialog !== null && (
            <CustomModelFormDialog
              model={customModelDialog === 'new' ? undefined : customModelDialog}
              onSave={async (data) => {
                if (customModelDialog === 'new') {
                  await onAddCustomModel(data);
                } else {
                  await onEditCustomModel(customModelDialog.id, data);
                }
                setCustomModelDialog(null);
              }}
              onClose={() => setCustomModelDialog(null)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── Add Provider Form ─────────────────────

function AddProviderForm({
  template,
  onSave,
  onCancel,
}: {
  template: ProviderTemplate;
  onSave: (body: {
    providerTemplateId: number;
    displayName?: string;
    config: Record<string, unknown>;
    defaultProviderModelId?: number;
    customModel?: CustomModelFormData;
  }) => Promise<void>;
  onCancel: () => void;
}) {
  const [displayName, setDisplayName] = useState('');
  const [config, setConfig] = useState<Record<string, string>>({});
  const [models, setModels] = useState<ProviderModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [selectedModelId, setSelectedModelId] = useState<number | undefined>(undefined);
  const [useCustomModel, setUseCustomModel] = useState(false);
  const [customModel, setCustomModel] = useState<CustomModelFormData>({
    name: '', slug: '', maxTokens: 4096, contextWindow: 128000,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch models for this template separately (matching hai_assistants pattern)
  useEffect(() => {
    let cancelled = false;
    setModelsLoading(true);
    fetchTemplateModels(template.id)
      .then((fetched) => {
        if (cancelled) return;
        setModels(fetched);
        // Auto-select default model
        const defaultModel = fetched.find((m) => m.isDefault) ?? fetched[0];
        if (defaultModel) setSelectedModelId(defaultModel.id);
      })
      .catch((err) => {
        if (!cancelled) setError(`Failed to load models: ${err.message}`);
      })
      .finally(() => {
        if (!cancelled) setModelsLoading(false);
      });
    return () => { cancelled = true; };
  }, [template.id]);

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      // Validate required config fields
      if (template.configSchema) {
        for (const [key, field] of Object.entries(template.configSchema)) {
          if (field.required && !config[key]) {
            setError(`${formatLabel(key)} is required`);
            setSaving(false);
            return;
          }
        }
      }

      // Validate custom model if enabled
      if (useCustomModel) {
        if (!customModel.name.trim()) { setError('Model name is required'); setSaving(false); return; }
        if (!customModel.slug.trim()) { setError('Model ID/Slug is required'); setSaving(false); return; }
        if (customModel.maxTokens < 1) { setError('Max tokens must be at least 1'); setSaving(false); return; }
        if (customModel.contextWindow < 1) { setError('Context window must be at least 1'); setSaving(false); return; }
        if (customModel.maxTokens >= customModel.contextWindow) {
          setError('Max tokens must be less than context window');
          setSaving(false);
          return;
        }
      }

      // Build typed config
      const typedConfig: Record<string, unknown> = {};
      if (template.configSchema) {
        for (const [key, field] of Object.entries(template.configSchema)) {
          const val = config[key];
          if (val === undefined || val === '') continue;
          if (field.type === 'number') typedConfig[key] = Number(val);
          else if (field.type === 'boolean') typedConfig[key] = val === 'true';
          else typedConfig[key] = val;
        }
      }

      await onSave({
        providerTemplateId: template.id,
        displayName: displayName.trim() || undefined,
        config: typedConfig,
        defaultProviderModelId: useCustomModel ? undefined : selectedModelId,
        customModel: useCustomModel ? customModel : undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add provider');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border bg-card overflow-hidden">
      <div className="px-3 py-2.5 border-b bg-muted/30">
        <p className="text-xs font-medium">Add {template.name}</p>
      </div>
      <div className="px-3 py-3 space-y-3">
        {error && (
          <div className="text-xs text-destructive bg-destructive/10 p-2 rounded">{error}</div>
        )}

        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Display Name</Label>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={template.name}
            className="text-xs h-8"
            maxLength={255}
          />
        </div>

        {/* Model selector or Custom model toggle */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium">Model</Label>
            <button
              type="button"
              className="text-[10px] text-primary hover:underline"
              onClick={() => setUseCustomModel(!useCustomModel)}
            >
              {useCustomModel ? 'Use existing model' : 'Use custom model'}
            </button>
          </div>

          {!useCustomModel && (
            modelsLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                <Loader2 className="size-3 animate-spin" />
                Loading models...
              </div>
            ) : models.length > 0 ? (
              <select
                value={selectedModelId ?? ''}
                onChange={(e) =>
                  setSelectedModelId(e.target.value ? Number(e.target.value) : undefined)
                }
                className="w-full h-8 px-3 rounded-md border border-input bg-background text-xs"
              >
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.slug})
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-[10px] text-muted-foreground">No models available for this provider.</p>
            )
          )}

          {useCustomModel && (
            <div className="space-y-2 pl-2 border-l-2 border-primary/20">
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">Model Name <span className="text-destructive">*</span></Label>
                <Input
                  value={customModel.name}
                  onChange={(e) => setCustomModel({ ...customModel, name: e.target.value })}
                  placeholder="My Custom Model"
                  className="text-xs h-7"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">Model ID / Slug <span className="text-destructive">*</span></Label>
                <Input
                  value={customModel.slug}
                  onChange={(e) => setCustomModel({ ...customModel, slug: e.target.value })}
                  placeholder="custom-model-v1"
                  className="text-xs h-7 font-mono"
                  maxLength={255}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">Max Tokens <span className="text-destructive">*</span></Label>
                  <Input
                    type="number"
                    value={customModel.maxTokens}
                    onChange={(e) => setCustomModel({ ...customModel, maxTokens: Number(e.target.value) || 0 })}
                    min={1}
                    className="text-xs h-7"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">Context Window <span className="text-destructive">*</span></Label>
                  <Input
                    type="number"
                    value={customModel.contextWindow}
                    onChange={(e) => setCustomModel({ ...customModel, contextWindow: Number(e.target.value) || 0 })}
                    min={1}
                    className="text-xs h-7"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">Description</Label>
                <Input
                  value={customModel.description ?? ''}
                  onChange={(e) => setCustomModel({ ...customModel, description: e.target.value || undefined })}
                  placeholder="Optional description"
                  className="text-xs h-7"
                />
              </div>
            </div>
          )}
        </div>

        {/* Config fields */}
        {template.configSchema && (
          <ConfigFields
            schema={template.configSchema}
            values={config}
            onChange={setConfig}
          />
        )}

        <div className="flex items-center gap-2 pt-1">
          <Button size="sm" onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <Loader2 className="size-3.5 mr-1.5 animate-spin" />
            ) : (
              <Plus className="size-3.5 mr-1.5" />
            )}
            Add Provider
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Read-Only Config Fields (for edit view) ─────────────────

function ReadOnlyConfigFields({
  schema,
  maskedValues,
}: {
  schema: ConfigSchema;
  maskedValues?: Record<string, unknown>;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium">Configuration</Label>
      <div className="space-y-1.5">
        {Object.entries(schema).map(([key, field]) => {
          const maskedValue = maskedValues?.[key];
          const displayValue = maskedValue ? String(maskedValue) : (field.sensitive ? '••••••••' : '—');
          return (
            <div key={key} className="flex items-center gap-2 px-2 py-1.5 rounded bg-muted/40 text-xs">
              <span className="text-muted-foreground min-w-0 shrink-0">{formatLabel(key)}</span>
              <span className="flex-1 text-right font-mono truncate text-muted-foreground/70">
                {displayValue}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Dynamic Config Fields (for add form) ─────────────────

function ConfigFields({
  schema,
  values,
  onChange,
}: {
  schema: ConfigSchema;
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
}) {
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});

  const updateField = (key: string, val: string) => {
    onChange({ ...values, [key]: val });
  };

  return (
    <div className="space-y-2.5">
      {Object.entries(schema).map(([key, field]) => {
        const value = values[key] ?? '';
        const isSensitive = field.sensitive;
        const show = showKeys[key] ?? false;

        return (
          <div key={key} className="space-y-1.5">
            <Label className="text-xs font-medium">
              {formatLabel(key)}
              {field.required && <span className="text-destructive ml-0.5">*</span>}
            </Label>
            <div className="flex gap-1.5">
              <Input
                type={isSensitive && !show ? 'password' : 'text'}
                value={value}
                onChange={(e) => updateField(key, e.target.value)}
                placeholder={field.description || key}
                className="text-xs h-8 font-mono"
              />
              {isSensitive && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 shrink-0"
                  onClick={() => setShowKeys((prev) => ({ ...prev, [key]: !show }))}
                >
                  {show ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                </Button>
              )}
            </div>
            {field.description && (
              <p className="text-[10px] text-muted-foreground">{field.description}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Custom Model Form Dialog ─────────────────

function CustomModelFormDialog({
  model,
  onSave,
  onClose,
}: {
  model?: ProviderModel;
  onSave: (data: CustomModelFormData) => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm] = useState<CustomModelFormData>({
    name: model?.name ?? '',
    slug: model?.slug ?? '',
    maxTokens: model?.maxTokens ?? 4096,
    contextWindow: model?.contextWindow ?? 128000,
    description: model?.description ?? '',
    isDefault: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    if (!form.name.trim()) { setError('Model name is required'); return; }
    if (!form.slug.trim()) { setError('Model ID/Slug is required'); return; }
    if (form.maxTokens < 1) { setError('Max tokens must be at least 1'); return; }
    if (form.contextWindow < 1) { setError('Context window must be at least 1'); return; }
    if (form.maxTokens >= form.contextWindow) {
      setError('Max tokens must be less than context window');
      return;
    }

    setSaving(true);
    try {
      await onSave(form);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save model');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-background rounded-lg border shadow-lg w-full max-w-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-sm font-semibold">
            {model ? 'Edit Custom Model' : 'Add Custom Model'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-accent rounded transition-colors">
            <X className="size-4 text-muted-foreground" />
          </button>
        </div>
        <div className="px-4 py-3 space-y-3">
          {error && (
            <div className="text-xs text-destructive bg-destructive/10 p-2 rounded">{error}</div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Model Name <span className="text-destructive">*</span></Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="My Custom Model"
              className="text-xs h-8"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Model ID / Slug <span className="text-destructive">*</span></Label>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="custom-model-v1"
              className="text-xs h-8 font-mono"
              maxLength={255}
            />
            <p className="text-[10px] text-muted-foreground">Must match the model identifier used by the provider's API</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Max Tokens <span className="text-destructive">*</span></Label>
              <Input
                type="number"
                value={form.maxTokens}
                onChange={(e) => setForm({ ...form, maxTokens: Number(e.target.value) || 0 })}
                min={1}
                className="text-xs h-8"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Context Window <span className="text-destructive">*</span></Label>
              <Input
                type="number"
                value={form.contextWindow}
                onChange={(e) => setForm({ ...form, contextWindow: Number(e.target.value) || 0 })}
                min={1}
                className="text-xs h-8"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Description</Label>
            <Input
              value={form.description ?? ''}
              onChange={(e) => setForm({ ...form, description: e.target.value || undefined })}
              placeholder="Optional description"
              className="text-xs h-8"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isDefault"
              checked={form.isDefault ?? false}
              onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
              className="rounded border-input"
            />
            <label htmlFor="isDefault" className="text-xs text-muted-foreground cursor-pointer">
              Set as default model
            </label>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t">
          <Button size="sm" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving}>
            {saving && <Loader2 className="size-3.5 mr-1.5 animate-spin" />}
            {model ? 'Update' : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  );
}
